import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckSquareIcon, DatabaseIcon, UsersIcon, LightbulbIcon, SettingsIcon, XIcon, BriefcaseIcon, ShieldCheckIcon, BellIcon, TriangleIcon, LayoutDashboardIcon, FileTextIcon } from './icons';
import { useAuthContext } from '../contexts/AuthContext';
import ConfirmationModal from './ConfirmationModal';
import { Participant, TeamMember } from '../types';

// Map view names to URL paths
const VIEW_TO_PATH: Record<string, string> = {
  dashboard: '/dashboard',
  tasks: '/tasks',
  data: '/data',
  participants: '/participants',
  team: '/team',
  insights: '/insights',
  deliverables: '/deliverables',
  settings: '/settings',
};

const PATH_TO_VIEW: Record<string, string> = Object.fromEntries(
  Object.entries(VIEW_TO_PATH).map(([k, v]) => [v, k])
);

// Per-section colour coding: each nav section gets its own active colour
const NAV_COLOURS: Record<string, { text: string; bg: string; border: string }> = {
  '/dashboard':    { text: '#7BBFAF', bg: '#E8F5F3', border: '#7BBFAF' },  // seafoam
  '/tasks':        { text: '#D4715A', bg: '#F7EDE9', border: '#D4715A' },  // ember — primary work destination
  '/data':         { text: '#7A93B5', bg: '#EAF0F7', border: '#7A93B5' },  // steel
  '/feed':         { text: '#C2AC8E', bg: '#F5F0EA', border: '#C2AC8E' },  // sand — immersive feed
  '/participants': { text: '#6E7A5A', bg: '#EBF0E6', border: '#6E7A5A' },  // sage — people/community
  '/team':         { text: '#E8A09A', bg: '#FDF0EF', border: '#E8A09A' },  // blush
  '/insights':     { text: '#7BBFAF', bg: '#E8F5F3', border: '#7BBFAF' },  // seafoam
  '/deliverables': { text: '#C2AC8E', bg: '#F5F0EA', border: '#C2AC8E' },  // clay
  '/settings':     { text: '#A09890', bg: '#F5F3F0', border: '#A09890' },  // neutral
  '/superadmin':   { text: '#D4715A', bg: '#F7EDE9', border: '#D4715A' },  // ember
  '/signal-map':   { text: '#7A93B5', bg: '#EAF0F7', border: '#7A93B5' },  // steel — signification
};

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  path: string;
  isActive: boolean;
  isExpanded: boolean;
  onNavigate?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, path, isActive, isExpanded, onNavigate }) => {
  const navigate = useNavigate();
  const colours = NAV_COLOURS[path] || NAV_COLOURS['/settings'];

  return (
    <button
      onClick={() => { navigate(path); onNavigate?.(); }}
      title={label}
      className={`flex items-center w-full h-[44px] border-l-2 transition-all duration-200 ${isActive
        ? ''
        : 'text-text-faint border-transparent hover:bg-app-bg hover:text-text-ink'
      } ${isExpanded ? 'px-4' : 'justify-center'}`}
      style={isActive ? { color: colours.text, backgroundColor: colours.bg, borderLeftColor: colours.border } : undefined}
      aria-current={isActive ? 'page' : undefined}
    >
      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">{icon}</div>
      {isExpanded && <span className="whitespace-nowrap ml-4 text-sm font-semibold lg:hidden">{label}</span>}
    </button>
  );
};

interface SidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  currentUser: Participant | TeamMember | null;
  onEnableNotifications: () => void;
  hasSignificationTasks?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isSidebarOpen, setIsSidebarOpen, currentUser, onEnableNotifications, hasSignificationTasks }) => {
  const { logout, claimsRole } = useAuthContext();
  const location = useLocation();
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Determine the active view from the current URL path
  const currentView = PATH_TO_VIEW[location.pathname] || 'feed';

  const mainNavItems = [
    { icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11a9 9 0 0 1 9 9" /><path d="M4 4a16 16 0 0 1 16 16" /><circle cx="5" cy="19" r="1" fill="currentColor" /></svg>, label: "Couch", path: '/feed' },
    { icon: <LayoutDashboardIcon className="w-5 h-5" />, label: "Dashboard", path: '/dashboard' },
    { icon: <CheckSquareIcon className="w-5 h-5" />, label: "Tasks", path: '/tasks' },
    { icon: <DatabaseIcon className="w-5 h-5" />, label: "Data", path: '/data' },
    { icon: <UsersIcon className="w-5 h-5" />, label: "Participants", path: '/participants' },
    { icon: <BriefcaseIcon className="w-5 h-5" />, label: "Team", path: '/team' },
    { icon: <LightbulbIcon className="w-5 h-5" />, label: "Insights", path: '/insights' },
    { icon: <FileTextIcon className="w-5 h-5" />, label: "Deliverables", path: '/deliverables' },
  ];

  return (
    <>
      {/* Overlay for mobile & expanded desktop drawer */}
      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 bg-action/20 backdrop-blur-sm z-40 transition-opacity duration-300 lg:hidden"
          aria-hidden="true"
        ></div>
      )}

      <aside
        data-tutorial-id="sidebar"
        className={`fixed top-0 left-0 h-full bg-white border-r border-app-border pt-3 pb-3 flex flex-col z-50 transform transition-all duration-300 ease-in-out ${isSidebarOpen ? 'w-64 translate-x-0 lg:w-[52px]' : 'w-64 -translate-x-full lg:w-[52px] lg:translate-x-0'
          }`}
      >
        <div
          className={`flex items-center mb-4 ${isSidebarOpen ? 'px-5 justify-between' : 'justify-center'}`}
          style={{ height: 48 }}
        >
          <div className="flex items-center gap-3">
            <svg width="28" height="28" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <circle cx="50" cy="50" r="50" fill="#D4715A"/>
            </svg>
            {isSidebarOpen && <h1 className="text-2xl font-bold text-text-ink tracking-tight whitespace-nowrap lg:hidden">Dekko</h1>}
          </div>
          {isSidebarOpen && (
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-2 rounded-lg text-action/50 hover:bg-action/5 lg:hidden"
            >
              <XIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        <nav className="flex-grow flex flex-col space-y-2">
          {mainNavItems.map(item => (
            <NavItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              path={item.path}
              isActive={location.pathname === item.path}
              isExpanded={isSidebarOpen}
              onNavigate={() => setIsSidebarOpen(false)}
            />
          ))}
          {hasSignificationTasks && (
            <NavItem
              icon={<TriangleIcon className="w-5 h-5" />}
              label="Signal Map"
              path="/signal-map"
              isActive={location.pathname === '/signal-map'}
              isExpanded={isSidebarOpen}
              onNavigate={() => setIsSidebarOpen(false)}
            />
          )}
        </nav>

        <div className="mt-auto flex flex-col space-y-2 pt-4 border-t border-app-border">
          {claimsRole === 'superadmin' && (
            <NavItem
              icon={<ShieldCheckIcon className="w-5 h-5" />}
              label="Super Admin"
              path="/superadmin"
              isActive={location.pathname.startsWith('/superadmin')}
              isExpanded={isSidebarOpen}
              onNavigate={() => setIsSidebarOpen(false)}
            />
          )}
          <NavItem
            icon={<SettingsIcon className="w-5 h-5" />}
            label="Settings"
            path="/settings"
            isActive={location.pathname === '/settings'}
            isExpanded={isSidebarOpen}
            onNavigate={() => setIsSidebarOpen(false)}
          />

          {/* User Avatar + Dropdown */}
          {currentUser && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                title={currentUser.name}
                className={`w-full h-[44px] flex items-center transition-colors hover:bg-app-bg ${isSidebarOpen ? 'px-4' : 'justify-center'}`}
              >
                <div className="w-8 h-8 rounded-full bg-action text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {getInitials(currentUser.name)}
                </div>
                {isSidebarOpen && (
                  <div className="ml-3 text-left min-w-0">
                    <p className="text-[13px] font-semibold text-text-ink truncate">{currentUser.name}</p>
                    <p className="text-[11px] text-text-muted truncate">{'email' in currentUser ? currentUser.email : 'Participant'}</p>
                  </div>
                )}
              </button>

              {showProfileMenu && (
                <div
                  className="absolute bottom-full left-full mb-2 ml-1 bg-white border border-app-border overflow-hidden z-[60]"
                  style={{ borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 180, padding: '8px 0' }}
                >
                  <div style={{ padding: '10px 14px 8px' }} className="border-b border-app-border">
                    <p className="text-[13px] font-semibold text-text-ink">{currentUser.name}</p>
                    <p className="text-[11px] text-text-muted">{'email' in currentUser ? currentUser.email : 'Participant'}</p>
                  </div>
                  <button
                    onClick={() => { onEnableNotifications(); setShowProfileMenu(false); }}
                    className="w-full flex items-center gap-2.5 text-left text-[13px] text-text-ink hover:bg-app-bg transition-colors cursor-pointer"
                    style={{ padding: '8px 14px' }}
                  >
                    <BellIcon className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
                    Enable Notifications
                  </button>
                  <button
                    onClick={() => { setShowLogoutConfirmation(true); setShowProfileMenu(false); }}
                    className="w-full flex items-center gap-2.5 text-left text-[13px] text-status-warn hover:bg-app-bg transition-colors cursor-pointer"
                    style={{ padding: '8px 14px' }}
                  >
                    <XIcon className="w-3.5 h-3.5 text-status-warn flex-shrink-0" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
      <ConfirmationModal
        isOpen={showLogoutConfirmation}
        onClose={() => setShowLogoutConfirmation(false)}
        onConfirm={logout}
        title="Sign Out"
        message="Ready to sign out? You'll need to sign back in to access your projects and research data."
        confirmButtonText="Sign Out"
      />
    </>
  );
};

export default Sidebar;